import { FC } from 'react';
import Card from '@/components/card';
import Text from '@/components/text';
import { Box, Stack } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { CARD_SIZES, CardSize, GridCard as GridCardProps } from './helpers';
import Icon from '@/components/icon';
import { IconType } from '../icon/helpers';
import classNames from 'classnames';

interface Props {
  gridCard: GridCardProps;
  size?: CardSize;
}

type StyleProps = { width: number; height: number };

const useStyles = createUseStyles<string, StyleProps>({
  gridCard: {
    width: ({ width }) => width,
    height: ({ height }) => height,
    '&.small': {
      '& $iconWrapper': {
        width: 40,
        height: 40,
      },
      '& $content': {
        width: 'auto',
        flex: 1,
        overflow: 'hidden',
      },
      '& $title': {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        wordWrap: 'normal',
        marginBottom: 0,
      },
      '& $detail': {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxHeight: 'none',
      },
    },
  },
  iconWrapper: {
    width: 'auto',
    height: 'auto',
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
    '& svg': {
      maxWidth: '100%',
      maxHeight: '100%',
    },
  },
  content: {
    minWidth: 0,
    width: '100%',
    flex: 'initial',
    overflow: 'visible',
  },
  title: {
    overflow: 'visible',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    marginBottom: 4,
  },
  detail: {
    overflow: 'hidden',
    textOverflow: 'clip',
    whiteSpace: 'normal',
    maxHeight: 90,
  },
});

const GridCard: FC<Props> = ({ gridCard, size = 'small' }) => {
  const { title, iconId, IconEl, detail, children, onClick } = gridCard;

  const isSmall = size === 'small';
  const { width, height } = CARD_SIZES[size];
  const styles = useStyles({ width, height });

  const hasIconId = !!iconId;
  const hasIconEl = !hasIconId && !!IconEl;
  const hasIcon = hasIconId || hasIconEl;
  const hasTitle = !!title;
  const hasDetail = !!detail;

  const compClass = classNames('grid-card', styles.gridCard, { small: isSmall });
  const titleCompClass = classNames('title', styles.title);

  return (
    <Card className={compClass} hover={!!onClick} onClick={onClick}>
      <Stack
        direction={isSmall ? 'row' : 'column'}
        alignItems={isSmall ? 'center' : 'flex-start'}
        gap={isSmall ? 1.5 : 0}
        height="100%"
        width="100%"
      >
        {hasIcon && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            mb={isSmall ? 0 : 1}
            className={styles.iconWrapper}
          >
            {hasIconId && <Icon name={IconEl as IconType} />}
            {hasIconEl && IconEl}
          </Stack>
        )}
        <Stack justifyContent="center" className={styles.content}>
          {hasTitle && (
            <Box width="100%" className={titleCompClass}>
              <Text dotdot>{title}</Text>
            </Box>
          )}
          {hasDetail && (
            <Text size="x-small" color="grey" className={styles.detail}>
              {detail}
            </Text>
          )}
        </Stack>
        {children}
      </Stack>
    </Card>
  );
};

export default GridCard;
